/**
 * Adapted from PlayCanvas Engine's gsplat-streams.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import type {
  GSplatGraphicsAdapter,
  GSplatTextureDimensions,
  GSplatTextureLeaf,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type { GSplatFormat, GSplatStreamDescriptor } from './GSplatFormat'

const BASE_STREAM_TEXTURE_USAGES = Object.freeze(['sampled', 'copy-src', 'copy-dst'] as const)

/** adapter GPU texture leaves 的事务型 owner。 */
export class GSplatStreams {
  private readonly adapter: GSplatGraphicsAdapter
  private readonly isInstance: boolean
  private currentFormat: GSplatFormat | null = null
  private readonly textures = new Map<string, GSplatTextureLeaf>()
  private readonly ownedTextureNames = new Set<string>()
  /** 被 format 删除或同名换格式后，延迟到整个 Streams retire 才释放。 */
  private readonly retiredOwnedTextures = new Set<GSplatTextureLeaf>()
  private dimensions: GSplatTextureDimensions = Object.freeze({ width: 0, height: 0 })
  private formatVersion = -1
  private initialized = false
  private destroyed = false

  constructor(adapter: GSplatGraphicsAdapter, isInstance = false) {
    this.adapter = adapter
    this.isInstance = isInstance
  }

  /** GPU leaves 所属的唯一 graphics device；Streams 不支持跨 adapter 转移。 */
  get graphicsAdapter(): GSplatGraphicsAdapter {
    return this.adapter
  }

  get format(): GSplatFormat | null {
    return this.currentFormat
  }

  get textureDimensions(): GSplatTextureDimensions {
    return this.dimensions
  }

  get extraStreamsVersion(): number {
    return this.formatVersion
  }

  isSynchronizedWithFormat(format: GSplatFormat): boolean {
    return this.currentFormat === format && this.formatVersion === format.extraStreamsVersion
  }

  setTextureDimensions(dimensions: GSplatTextureDimensions): void {
    this.assertActive()
    this.validateDimensions(dimensions)
    this.dimensions = Object.freeze({ ...dimensions })
  }

  init(format: GSplatFormat, numElements: number): void {
    this.assertActive()
    if (this.initialized) throw new Error('GSplatStreams.init 每个实例只能成功一次')
    const dimensions = this.preflight(format, numElements)
    const staged = this.stageTextures(this.getStreams(format), dimensions)
    this.publish(format, dimensions, staged)
    for (const name of staged.keys()) this.ownedTextureNames.add(name)
    this.initialized = true
  }

  /**
   * 在创建任一 texture leaf 前验证完整 stream 布局。
   *
   * WorkBuffer 会先调用本入口预检 payload textures 与 order buffer，避免先分配若干 storage
   * texture 后才因末尾的 order limit 失败。实际 stage 仍会重复预检，以防 format 在两步之间变化。
   */
  preflight(format: GSplatFormat, numElements: number): GSplatTextureDimensions {
    this.assertActive()
    if (!Number.isSafeInteger(numElements) || numElements < 0) {
      throw new Error(`非法 GSplatStreams numElements: ${numElements}`)
    }
    const dimensions = this.adapter.calculateTextureDimensions(numElements)
    this.validateDimensions(dimensions)
    this.preflightTextures(this.getStreams(format), dimensions)
    return Object.freeze({ ...dimensions })
  }

  /**
   * SOG 等格式把 Data-owned source textures 借给 Streams 自动绑定。
   * 本方法不接管 leaf 所有权，destroy 前后都不会 dispose 这些借用纹理。
   */
  attachBorrowed(
    format: GSplatFormat,
    dimensions: GSplatTextureDimensions,
    borrowed: ReadonlyMap<string, GSplatTextureLeaf>
  ): void {
    this.assertActive()
    if (this.initialized) throw new Error('GSplatStreams 每个实例只能成功初始化一次')
    this.validateDimensions(dimensions)
    const expected = this.getStreams(format)
    if (borrowed.size !== expected.length) {
      throw new Error(
        `GSplatStreams borrowed texture 数量不匹配，expected=${expected.length}, actual=${borrowed.size}`
      )
    }
    for (const stream of expected) {
      const texture = borrowed.get(stream.name)
      if (!texture) throw new Error(`GSplatStreams 缺少 borrowed texture: ${stream.name}`)
      if (texture.format !== stream.format) {
        throw new Error(
          `GSplatStreams borrowed texture format 不匹配: ${stream.name}, expected=${stream.format}, actual=${texture.format}`
        )
      }
      if (texture.width < 1 || texture.height < 1) {
        throw new Error(`GSplatStreams borrowed texture 尺寸非法: ${stream.name}`)
      }
    }
    this.publish(format, dimensions, new Map(borrowed))
    this.initialized = true
  }

  /** 在 Data owner 销毁借用纹理前，从 Streams map 原子移除，防止 base 重复释放。 */
  detachBorrowed(): void {
    for (const name of [...this.textures.keys()]) {
      if (!this.ownedTextureNames.has(name)) this.textures.delete(name)
    }
  }

  syncWithFormat(format: GSplatFormat | null): void {
    this.assertActive()
    if (!format) return
    if (this.currentFormat === format && this.formatVersion === format.extraStreamsVersion) return

    this.validateDimensions(this.dimensions)
    if (this.currentFormat && this.currentFormat !== format) {
      if ([...this.textures.keys()].some((name) => !this.ownedTextureNames.has(name))) {
        throw new Error('GSplatStreams borrowed textures 不支持 format identity replacement')
      }
      const staged = this.stageTextures(this.getStreams(format), this.dimensions)
      const oldTextures = Array.from(this.textures.values())
      const oldRetiredTextures = Array.from(this.retiredOwnedTextures)
      this.textures.clear()
      for (const [name, texture] of staged) this.textures.set(name, texture)
      this.ownedTextureNames.clear()
      this.retiredOwnedTextures.clear()
      for (const name of staged.keys()) this.ownedTextureNames.add(name)
      this.currentFormat = format
      this.formatVersion = format.extraStreamsVersion
      for (const texture of oldTextures) texture.dispose()
      for (const texture of oldRetiredTextures) texture.dispose()
      return
    }

    const staged = new Map<string, GSplatTextureLeaf>()
    try {
      for (const stream of this.getStreams(format)) {
        const current = this.textures.get(stream.name)
        if (current?.format === stream.format) continue
        staged.set(stream.name, this.createTexture(stream, this.dimensions))
      }
    } catch (error) {
      for (const texture of staged.values()) texture.dispose()
      throw error
    }

    for (const [name, texture] of staged) {
      const previous = this.textures.get(name)
      if (previous && this.ownedTextureNames.has(name)) this.retiredOwnedTextures.add(previous)
      this.textures.set(name, texture)
    }
    for (const name of staged.keys()) this.ownedTextureNames.add(name)
    this.currentFormat = format
    this.formatVersion = format.extraStreamsVersion
  }

  getTexture(name: string): GSplatTextureLeaf | undefined {
    this.syncWithFormat(this.currentFormat)
    return this.textures.get(name)
  }

  getTexturesInOrder(): readonly GSplatTextureLeaf[] {
    if (!this.currentFormat) return Object.freeze([])
    const result: GSplatTextureLeaf[] = []
    for (const stream of this.getStreams(this.currentFormat)) {
      const texture = this.textures.get(stream.name)
      if (texture) result.push(texture)
    }
    return Object.freeze(result)
  }

  resize(width: number, height: number): void {
    this.assertActive()
    if (!this.currentFormat) throw new Error('GSplatStreams.resize 要求已绑定 format')
    const dimensions = { width, height }
    this.validateDimensions(dimensions)
    if (this.dimensions.width === width && this.dimensions.height === height) return
    if ([...this.textures.keys()].some((name) => !this.ownedTextureNames.has(name))) {
      throw new Error('GSplatStreams borrowed textures 不支持统一 resize')
    }

    const staged = this.stageTextures(this.getStreams(this.currentFormat), dimensions)
    const oldTextures = Array.from(this.textures.values())
    this.textures.clear()
    for (const [name, texture] of staged) this.textures.set(name, texture)
    this.ownedTextureNames.clear()
    for (const name of staged.keys()) this.ownedTextureNames.add(name)
    this.dimensions = Object.freeze({ ...dimensions })
    for (const texture of oldTextures) texture.dispose()
  }

  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    for (const [name, texture] of this.textures) {
      if (this.ownedTextureNames.has(name)) texture.dispose()
    }
    for (const texture of this.retiredOwnedTextures) texture.dispose()
    this.textures.clear()
    this.ownedTextureNames.clear()
    this.retiredOwnedTextures.clear()
    this.currentFormat = null
    this.formatVersion = -1
  }

  private getStreams(format: GSplatFormat): readonly GSplatStreamDescriptor[] {
    return this.isInstance ? format.instanceStreams : format.resourceStreams
  }

  private stageTextures(
    streams: readonly GSplatStreamDescriptor[],
    dimensions: GSplatTextureDimensions
  ): Map<string, GSplatTextureLeaf> {
    this.preflightTextures(streams, dimensions)
    const staged = new Map<string, GSplatTextureLeaf>()
    try {
      for (const stream of streams) {
        staged.set(stream.name, this.createTexture(stream, dimensions))
      }
      return staged
    } catch (error) {
      for (const texture of staged.values()) texture.dispose()
      throw error
    }
  }

  private createTexture(
    stream: GSplatStreamDescriptor,
    dimensions: GSplatTextureDimensions
  ): GSplatTextureLeaf {
    return this.adapter.createTexture(this.createTextureDescriptor(stream, dimensions))
  }

  private preflightTextures(
    streams: readonly GSplatStreamDescriptor[],
    dimensions: GSplatTextureDimensions
  ): void {
    for (const stream of streams) {
      this.adapter.validateTextureDescriptor(this.createTextureDescriptor(stream, dimensions))
    }
  }

  private createTextureDescriptor(
    stream: GSplatStreamDescriptor,
    dimensions: GSplatTextureDimensions
  ) {
    const usages = this.adapter.getTextureFormatInfo(stream.format).renderable
      ? Object.freeze([...BASE_STREAM_TEXTURE_USAGES, 'render-attachment'] as const)
      : BASE_STREAM_TEXTURE_USAGES
    return {
      label: stream.name,
      width: dimensions.width,
      height: dimensions.height,
      format: stream.format,
      usages,
    }
  }

  private publish(
    format: GSplatFormat,
    dimensions: GSplatTextureDimensions,
    staged: Map<string, GSplatTextureLeaf>
  ): void {
    this.currentFormat = format
    this.dimensions = Object.freeze({ ...dimensions })
    for (const [name, texture] of staged) this.textures.set(name, texture)
    this.formatVersion = format.extraStreamsVersion
  }

  private validateDimensions(dimensions: GSplatTextureDimensions): void {
    const { width, height } = dimensions
    if (
      !Number.isSafeInteger(width) ||
      !Number.isSafeInteger(height) ||
      width < 1 ||
      height < 1 ||
      width > this.adapter.limits.maxTextureDimension2D ||
      height > this.adapter.limits.maxTextureDimension2D
    ) {
      throw new Error(`非法 GSplat texture dimensions: ${width}x${height}`)
    }
  }

  private assertActive(): void {
    if (this.destroyed) throw new Error('GSplatStreams 已销毁')
  }
}
